import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import Stack from '~/common/components/Stacks/Stack';

import SnapShotButton from '../components/smartQuestionComponents/SnapshotButton';
import StandardFormControl from '../components/utilityComponents/StandardFormControl';
import InputMapper from '../mappers/InputMapper';
import { useQuestionLibraryStore } from '../store/questionLibraryStore';
import buildRules from '../utils/buildRules';
import { SMART_ANSWER_LABEL_TYPES } from '../utils/constants';
import { getError, hasError } from '../utils/errorHandling';
import { useSelectSmartAnswer } from '../utils/hooks';
import { useConditionsEvaluator } from '../utils/useConditionsEvaluator';
import { formatFieldName } from '../utils/utils';

type InputControllerProps = {
  question?: any;
  onFocus?: (...args: any[]) => any;
  disabled?: boolean;
  isRequirable?: boolean;
  isParentShowing?: boolean;
  showIsRequired?: boolean;
  smartAnswerLayout?: string;
  padding?: string;
  maxRowWidth?: string;
  metadata?: Record<string, unknown>;
  maxInputWidth?: string;
};

/**
 * @typedef {object} InputControllerProps
 * @property {import('../types/question').QuestionInput} question
 * @property {boolean} disabled
 * @property {function} onFocus
 *
 * @param {InputControllerProps} props
 * @returns {JSX.Element}
 */
export default function InputController({
  question,
  disabled = false,
  onFocus,
  isRequirable = false,
  isParentShowing = true,
  showIsRequired = false,
  smartAnswerLayout = '',
  padding = '0 0 0.5rem 0',
  maxRowWidth,
  metadata = {},
  ...props
}: Readonly<InputControllerProps>) {
  const questionLibraryScreenData = useQuestionLibraryStore(
    (s) => s.screenData,
  );
  const { isVisible, isDisabled } = useConditionsEvaluator(
    question.conditions ?? {},
    metadata,
  );
  const isAiHidden =
    questionLibraryScreenData?.isAiHiddenByQuestion?.[question.name] === true;
  const isShowing = isVisible && isParentShowing;
  const {
    control,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext();
  const rules = isRequirable
    ? buildRules(question, isShowing, {
        screenData: questionLibraryScreenData,
        formData: watch(),
      })
    : {};

  const { field } = useController({
    name: question.name,
    control,
    rules: rules,
  });

  const resolvedLayout = question.smartAnswerLayout || smartAnswerLayout;
  const storeSmartAnswer = useSelectSmartAnswer(
    question.smartAnswerGuid ?? question.name,
  );

  useEffect(() => {
    clearErrors(formatFieldName(field.name));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isRequirable]);

  if (isAiHidden) {
    return null;
  }

  return (
    <Stack direction="row" spacing="12px" sx={{ alignItems: 'flex-end' }}>
      <StandardFormControl
        error={getError(errors, field.name)}
        field={field}
        onFocus={onFocus}
        padding={padding}
        smartAnswerLayout={question.smartAnswerLayout || smartAnswerLayout}
        showIsRequired={showIsRequired}
        errorPosition="absolute"
        maxRowWidth={maxRowWidth}
        {...question}
      >
        {' '}
        <InputMapper
          inputType={question.type}
          hasError={hasError(errors, field.name)}
          field={field}
          question={question}
          disabled={disabled || isDisabled}
          aria-labelledby={`${question.name}-label`}
          {...props}
        />
      </StandardFormControl>
      {resolvedLayout === SMART_ANSWER_LABEL_TYPES.ICON_ONLY && (
        <SnapShotButton
          snippets={storeSmartAnswer?.aiResponse?.snippets}
          questionName={question.name}
        />
      )}
    </Stack>
  );
}
